import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CBadge, CCard, CCardBody, CCardText, CCol, CRow, CSpinner } from '@coreui/react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import apiClient from '../../services/apiClient'

import useAccountContext from '../../hooks/useAccountContext'
import {
  RecommendationCardView,
  RecommendationSkeleton,
} from '../dashboard/components/RecommendationCard'
import getReminderNextSteps from '../../utils/reminderPlanner'
import updateScore from '../../utils/updateScore'
import { postponeReminder, updateReminderState } from '../../services/remindersService'
import { logError, logEvent } from '../../utils/telemetry'
import { addEvent } from '../../utils/calendar'
import { buildApiUrl } from '../../utils/getConfiguration'
import { normalizeLanguage } from '../../utils/i18nConfig'
import { useTranslation } from 'react-i18next'

const gradientCardStyle = {
  background: 'linear-gradient(135deg, #ffffffff, #ddddfdff)',
  color: '#4a4a4a',
}

const WidgetTop3Short = ({ grp_ID, onDatasetChange = null, syncToken = 0 }) => {
  const { t, i18n } = useTranslation()
  const { accountId } = useAccountContext()
  const [loading, setLoading] = useState(false)
  const [topRecommendations, setTopRecommendations] = useState([])
  const [totalRecommendations, setTotalRecommendations] = useState(0)
  const [error, setError] = useState(null)
  const controllerRef = useRef(null)
  const allControllerRef = useRef(null)
  const lastReminderRef = useRef(null)
  const lastSupplierRef = useRef({
    supplier_ID: null,
    supplier_contact_tel: null,
    supplier_name: null,
  })
  const lastExpectedMonthRef = useRef(null)
  const syncEffectGuardRef = useRef(true)
  const navigate = useNavigate()

  const selectReminder = useCallback((reminderId) => {
    if (typeof window === 'undefined' || !reminderId) return
    if (lastReminderRef.current === reminderId) return
    lastReminderRef.current = reminderId
    localStorage.setItem('taskid', reminderId)
  }, [])

  const setSupplierContext = useCallback((recommendation) => {
    if (typeof window === 'undefined' || !recommendation) return
    const next = {
      supplier_ID: recommendation.supplier_ID || '',
      supplier_contact_tel: recommendation.supplier_contact_tel || '',
      supplier_name: recommendation.supplier_name || '',
    }
    if (
      lastSupplierRef.current.supplier_ID === next.supplier_ID &&
      lastSupplierRef.current.supplier_contact_tel === next.supplier_contact_tel &&
      lastSupplierRef.current.supplier_name === next.supplier_name
    ) {
      return
    }
    lastSupplierRef.current = next
    localStorage.setItem('supplierid', next.supplier_ID)
    localStorage.setItem('suppliertel', next.supplier_contact_tel)
    localStorage.setItem('suppliername', next.supplier_name)
  }, [])

  const setExpectedMonth = useCallback((value) => {
    if (typeof window === 'undefined') return
    if (lastExpectedMonthRef.current === value) return
    lastExpectedMonthRef.current = value
    localStorage.setItem('AImonth', String(value))
  }, [])

  const readLocalStorage = useCallback((key) => {
    if (typeof window === 'undefined') return ''
    return window.localStorage.getItem(key) || ''
  }, [])

  const removeRecommendation = useCallback((reminderId) => {
    setTopRecommendations((prev) =>
      prev.filter((item) => String(item.reminder_ID) !== String(reminderId)),
    )
  }, [])

  const fetchTopRecommendations = useCallback(async () => {
    if (controllerRef.current) controllerRef.current.abort()
    const controller = new AbortController()
    controllerRef.current = controller

    if (!accountId || !grp_ID) {
      setTopRecommendations([])
      return
    }

    setLoading(true)
    setError(null)
    logEvent('top3:fetch:start', { accountId, grp_ID })

    try {
      const response = await apiClient.get(
        buildApiUrl('remindersTop3', {
          account_ID: accountId,
          grp_ID,
        }),
        {
          signal: controller.signal,
        },
      )
      const next = Array.isArray(response.data) ? response.data : []
      setTopRecommendations(next)
      logEvent('top3:fetch:success', { count: next.length })
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        return
      }
      logError('top3:fetch:error', err)
      setError(t('top3.errors.load'))
    } finally {
      if (controllerRef.current === controller) {
        setLoading(false)
      }
    }
  }, [accountId, grp_ID, t])

  const fetchTotalRecommendations = useCallback(async () => {
    if (allControllerRef.current) allControllerRef.current.abort()
    const controller = new AbortController()
    allControllerRef.current = controller

    if (!accountId) {
      setTotalRecommendations(0)
      return
    }

    try {
      const response = await apiClient.get(buildApiUrl('reminders', { account_ID: accountId }), {
        signal: controller.signal,
      })
      const all = Array.isArray(response.data) ? response.data : []
      setTotalRecommendations(all.length)
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        return
      }
      logError('top3:fetch:total:error', err)
    }
  }, [accountId])

  useEffect(() => {
    fetchTopRecommendations()
    return () => {
      if (controllerRef.current) controllerRef.current.abort()
    }
  }, [fetchTopRecommendations])

  useEffect(() => {
    fetchTotalRecommendations()
    return () => {
      if (allControllerRef.current) allControllerRef.current.abort()
    }
  }, [fetchTotalRecommendations])

  const refreshData = useCallback(async () => {
    await Promise.all([fetchTopRecommendations(), fetchTotalRecommendations()])
  }, [fetchTopRecommendations, fetchTotalRecommendations])

  const syncDatasets = useCallback(async () => {
    if (onDatasetChange) {
      await onDatasetChange()
    } else {
      await refreshData()
    }
  }, [onDatasetChange, refreshData])

  useEffect(() => {
    if (syncEffectGuardRef.current) {
      syncEffectGuardRef.current = false
      return
    }
    refreshData()
  }, [refreshData, syncToken])

  const performReminderStatusUpdate = useCallback(
    async (state, reminderId) => {
      const effectiveReminderId = reminderId || readLocalStorage('taskid')
      if (!effectiveReminderId || !accountId) {
        console.warn('Missing reminder or account context, state update skipped.')
        return false
      }
      try {
        await updateReminderState({ reminderId: effectiveReminderId, state, accountId })
        return true
      } catch (error) {
        logError('top3:updateReminderState', error, { reminderId: effectiveReminderId, state })
        return false
      }
    },
    [accountId, readLocalStorage],
  )

  const handleClickPostpone = useCallback(
    async (reminderId, nextExpectedMonth, options = {}) => {
      const effectiveReminderId = reminderId || readLocalStorage('taskid')
      const expectedMonth =
        typeof nextExpectedMonth === 'number' && !Number.isNaN(nextExpectedMonth)
          ? nextExpectedMonth
          : Number(readLocalStorage('AImonth')) || nextExpectedMonth

      if (!effectiveReminderId || !accountId) {
        console.warn('Missing context, postpone skipped.')
        return false
      }

      if (Number.isFinite(expectedMonth)) {
        setExpectedMonth(expectedMonth)
      }

      try {
        await postponeReminder({
          reminderId: effectiveReminderId,
          expecteddate: Number.isFinite(expectedMonth) ? String(expectedMonth) : '',
          accountId,
        })
        if (Number.isFinite(expectedMonth)) {
          setTopRecommendations((prev) =>
            prev.map((item) =>
              String(item.reminder_ID) === String(effectiveReminderId)
                ? { ...item, expecteddate: String(expectedMonth) }
                : item,
            ),
          )
        }
        logEvent('top3:postponed', { reminderId: effectiveReminderId, expectedMonth })
        if (!options.skipSync) {
          await syncDatasets()
        } else if (!onDatasetChange) {
          await refreshData()
        }
        return true
      } catch (error) {
        logError('top3:postpone:error', error, { reminderId: effectiveReminderId, expectedMonth })
        return false
      }
    },
    [accountId, onDatasetChange, readLocalStorage, refreshData, setExpectedMonth, syncDatasets],
  )

  const handleClickSwitchOff = useCallback(
    async (recommendation) => {
      const reminderId = recommendation?.reminder_ID
      const updated = await performReminderStatusUpdate('S', reminderId)
      if (updated && reminderId) {
        removeRecommendation(reminderId)
        logEvent('top3:switchOff', { reminderId })
        await syncDatasets()
      }
    },
    [performReminderStatusUpdate, removeRecommendation, syncDatasets],
  )

  const handleClickIgnore = useCallback(
    async (recommendation) => {
      const reminderId = recommendation?.reminder_ID
      const updated = await performReminderStatusUpdate('I', reminderId)
      if (updated && reminderId) {
        removeRecommendation(reminderId)
        logEvent('top3:ignored', { reminderId })
        await syncDatasets()
      }
    },
    [performReminderStatusUpdate, removeRecommendation, syncDatasets],
  )

  const handleClickDone = useCallback(
    async (recommendation) => {
      if (!recommendation) return
      const plan = getReminderNextSteps(recommendation)
      const reminderId = recommendation.reminder_ID
      let shouldSync = false

      if (plan.postponeMonth !== null) {
        setExpectedMonth(plan.postponeMonth)
      }

      if (plan.shouldPostpone && plan.postponeBeforeComplete) {
        const postponed = await handleClickPostpone(reminderId, plan.postponeMonth, {
          skipSync: true,
        })
        shouldSync = shouldSync || postponed
      }

      if (plan.shouldComplete) {
        const updated = await performReminderStatusUpdate('D', reminderId)
        if (updated) {
          const points = Number(recommendation.points)
          const groupId = Number(recommendation.grp_ID)
          try {
            await updateScore(
              Number.isFinite(points) ? points : 0,
              Number.isFinite(groupId) ? groupId : 0,
            )
          } catch (error) {
            logError('top3:updateScore:error', error, { reminderId })
          }
          removeRecommendation(reminderId)
          logEvent('top3:completed', { reminderId, points: recommendation.points })
          shouldSync = true
        }
      }

      if (plan.shouldPostpone && !plan.postponeBeforeComplete && !plan.shouldComplete) {
        await handleClickPostpone(reminderId, plan.postponeMonth)
        return
      }

      if (shouldSync) {
        await syncDatasets()
      }
    },
    [
      handleClickPostpone,
      performReminderStatusUpdate,
      removeRecommendation,
      setExpectedMonth,
      syncDatasets,
    ],
  )

  const handleDetailNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      navigate('/detail')
    },
    [selectReminder, navigate],
  )

  const handleSupplierNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      setSupplierContext(recommendation)
      navigate('/suppliers')
    },
    [navigate, selectReminder, setSupplierContext],
  )

  const handleEshopNavigation = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      navigate('/eshop')
    },
    [navigate, selectReminder],
  )

  const handleCalendarAdd = useCallback(
    (recommendation) => {
      if (!recommendation) return
      selectReminder(recommendation.reminder_ID)
      addEvent(recommendation.Title, recommendation.Description)
    },
    [selectReminder],
  )

  const handleInnovationSuppliers = useCallback(() => {
    navigate('/suppliersinno')
  }, [navigate])

  const handleInnovationStatus = useCallback(() => {
    navigate('/suppliersinnoprojectstatus')
  }, [navigate])

  const language = useMemo(() => normalizeLanguage(i18n.language || 'en'), [i18n.language])
  const intlLocale = useMemo(() => (language === 'cz' ? 'cs' : language), [language])
  const now = useMemo(() => new Date(), [])
  const formattedMonth = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { month: 'long' }).format(now),
    [intlLocale, now],
  )
  const formattedYear = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { year: 'numeric' }).format(now),
    [intlLocale, now],
  )

  const widgetName = useMemo(() => {
    switch (Number(grp_ID)) {
      case 1:
        return t('top3.areas.house')
      case 2:
        return t('top3.areas.flat')
      default:
        return t('top3.areas.area')
    }
  }, [grp_ID, t])

  const smColumns = useMemo(
    () => Math.min(Math.max(topRecommendations.length, 1), 3),
    [topRecommendations.length],
  )

  return (
    <CCard className="shadow-lg elevation-hover border-0" style={gradientCardStyle}>
      <CCardBody>
        <CRow className="align-items-center justify-content-between g-2">
          <CCol xs="auto">
            <CBadge color="primary" className="text-uppercase fw-semibold">
              {t('top3.badge')}
            </CBadge>
          </CCol>
          <CCol xs="auto" className="text-muted small">
            {t('top3.date', { year: formattedYear, month: formattedMonth })}
          </CCol>
        </CRow>
        <CRow className="mt-2">
          <CCol>
            <CCardText className="text-secondary mb-0">
              {t('top3.subtitle', { area: widgetName, total: totalRecommendations })}
            </CCardText>
          </CCol>
        </CRow>
        <hr />
        {error && (
          <CRow className="mb-3">
            <CCol>
              <CBadge color="danger">{error}</CBadge>
            </CCol>
          </CRow>
        )}
        {loading && topRecommendations.length === 0 ? (
          <RecommendationSkeleton />
        ) : topRecommendations.length > 0 ? (
          <CRow xs={{ cols: 1 }} sm={{ cols: smColumns }} className="g-3">
            {topRecommendations.map((recommendation) => (
              <CCol key={recommendation.reminder_ID}>
                <RecommendationCardView
                  recommendation={recommendation}
                  onDetail={handleDetailNavigation}
                  onSupplier={handleSupplierNavigation}
                  onEshop={handleEshopNavigation}
                  onCalendar={handleCalendarAdd}
                  onSwitchOff={handleClickSwitchOff}
                  onIgnore={handleClickIgnore}
                  onDone={handleClickDone}
                  onInnovationSuppliers={handleInnovationSuppliers}
                  onInnovationStatus={handleInnovationStatus}
                />
              </CCol>
            ))}
          </CRow>
        ) : (
          <CBadge color="info" className="mt-3">
            {t('top3.empty')}
          </CBadge>
        )}
        {loading && topRecommendations.length > 0 && (
          <div className="text-center py-3">
            <CSpinner size="sm" variant="grow" color="primary" />
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

WidgetTop3Short.propTypes = {
  grp_ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onDatasetChange: PropTypes.func,
  syncToken: PropTypes.number,
}

export default WidgetTop3Short
